import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { Search, HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { cn, toTitleCase } from '@/lib/utils';
import { CollapsibleAdvancedSearch } from './CollapsibleAdvancedSearch';

interface SearchDropdownProps {
  placeholder?: string;
  showAdvancedSearch?: boolean;
  className?: string;
  size?: 'sm' | 'lg';
}

interface SearchResult {
  soc_code: string;
  occ_title: string;
  median_salary: number;
  employment: number;
  no_degree_required: boolean;
  slug: string;
  /** How the API matched this row: title_exact | alias_exact | title_fuzzy | alias_fuzzy */
  matched_via?: string | null;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  placeholder = "Search by job title or SOC code (e.g., 'marketing manager' or '11-2021')",
  showAdvancedSearch = false,
  className,
  size = 'lg'
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch search suggestions
  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['search-suggestions', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];
      
      const { data, error } = await supabase.functions.invoke('api-occupations', {
        body: { 
          search: debouncedQuery,
          limit: 8
        }
      });
      
      if (error) throw error;
      return data?.occupations || [];
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000 // reuse suggestion results within a session
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const handleSearch = () => {
    if (!query.trim()) return;
    setIsOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  const handleSelectOccupation = (occupation: SearchResult) => {
    setIsOpen(false);
    setQuery('');
    navigate(`/salary/${occupation.slug}/united-states`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const inputSize = size === 'lg' ? 'py-3 text-lg' : 'py-2';

  // Everyday titles ("parole officer") resolve to official BLS titles that look
  // nothing like what was typed, so say so rather than showing a bare list.
  const topMatch: string | null | undefined = searchResults?.[0]?.matched_via;
  const showClosestMatchHint = Boolean(topMatch) && topMatch !== 'title_exact';

  return (
    <div className={cn("relative w-full", className)} ref={dropdownRef}>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder={placeholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(e.target.value.length >= 2);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= 2 && setIsOpen(true)}
            className={cn(
              "pl-10 bg-background text-foreground border border-input shadow-sm",
              size === 'lg' ? "py-3 text-lg" : "py-2"
            )}
          />
          {/* SOC Code Help Text */}
          {query.match(/^\d{2}-?\d{0,4}$/) && (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <HelpCircle className="h-3 w-3" />
              Searching by SOC code?{' '}
              <Link to="/what-is-soc-code/" className="text-primary hover:underline">
                Learn more
              </Link>
            </p>
          )}
        </div>
        
        <Button 
          size={size === 'lg' ? 'lg' : 'default'}
          className="bg-wage-accent hover:bg-wage-accent/90 text-white px-6 w-full sm:w-auto"
          onClick={handleSearch}
        >
          Search
        </Button>
      </div>

      {/* Dropdown */}
      {isOpen && (query.length >= 2) && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-popover border border-border rounded-md shadow-lg">
          <Command>
            <CommandList className="max-h-80">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Searching...
                </div>
              ) : searchResults?.length === 0 ? (
                <CommandEmpty>No occupations found.</CommandEmpty>
              ) : (
                <CommandGroup
                  heading={
                    showClosestMatchHint
                      ? `Closest official matches for "${debouncedQuery.trim()}"`
                      : undefined
                  }
                >
                  {searchResults?.map((result: SearchResult) => (
                    <CommandItem
                      key={result.soc_code}
                      onSelect={() => handleSelectOccupation(result)}
                      className="flex flex-col items-start p-4 cursor-pointer hover:bg-muted/50"
                    >
                      <div className="flex items-center justify-between w-full mb-1">
                        <span className="font-medium text-base">{toTitleCase(result.occ_title)}</span>
                        <span className="text-sm font-semibold text-wage-primary">
                          {result.median_salary ? formatCurrency(result.median_salary) : 'N/A'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>SOC: {result.soc_code}</span>
                        <span>•</span>
                        <span>{formatNumber(result.employment)} jobs</span>
                        {result.no_degree_required && (
                          <>
                            <span>•</span>
                            <Badge variant="secondary" className="text-xs">
                              No Degree
                            </Badge>
                          </>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </div>
      )}

      {showAdvancedSearch && (
        <div className="mt-4">
          <CollapsibleAdvancedSearch />
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;